import { Machine } from "@/machines/types/machine.ts";
import { generatePath, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber, formatTime } from "@/shared/lib/i18n/formatters.ts";
import { Check, Search } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { MACHINES_URL } from "@/machines/routes.ts";
import { TruckEventOverview } from "@/machines/types/event.ts";

interface MachineCardProps {
  machine: Machine;
  metadata?: TruckEventOverview["metadata"];
  isKpiLoading?: boolean;
}

const MachineCard = ({ machine, metadata, isKpiLoading = false }: MachineCardProps) => {
  const { t } = useTranslation("machines");
  const isOnline = machine.status === "online";

  const formatLastSeen = (lastSeen: Date | null): string => {
    if (!lastSeen) return t("card.lastSeenNever");
    try {
      if (isNaN(lastSeen.getTime())) return t("card.invalidDate");
      return formatTime(lastSeen);
    } catch {
      return t("card.invalidDate");
    }
  };

  const formatKpiValue = (value: number | undefined | null, formatType: 'percentage' | 'number' | 'time' = 'number'): string => {
    if (value == null || value === undefined) return "-";
    if (typeof value !== 'number' || !Number.isFinite(value)) return "-";

    switch (formatType) {
      case 'percentage':
        return t("units.percent", { value: formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) });
      case 'time': {
        // Convert seconds to minutes
        const minutes = Math.round(value / 60);
        return t("units.minutesCompact", { value: formatNumber(minutes) });
      }
      case 'number':
      default:
        return formatNumber(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
  };

  const truckLoading = metadata?.truckLoading;
  const waitingForTruck = metadata?.waitingForTruck;

  return (
    <div className="flex w-full flex-col gap-3  justify-between">
      <div>
        <div className={"flex flex-row items-center justify-between"}>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}>
              {isOnline && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="font-semibold text-base">{machine.name}</span>
            {/* Status Indicator */}
          </div>
          <span className="text-xs text-muted-foreground">
            {t("card.lastSeen", { time: formatLastSeen(machine.last_seen) })}
          </span>
        </div>
          <Link to={generatePath(MACHINES_URL.MACHINE_DETAIL, { id: machine.id.toString() })}>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Search className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Main Content: KPIs on left, Search icon on right */}
      <div className="flex gap-3 flex-1  items-end">
        {/* Left Side - KPI Metrics */}
        <div className="flex-1 flex flex-col gap-1.5">
          {isKpiLoading ? (
            // Loading skeleton
            Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="flex justify-between items-center text-xs">
                <div className="h-3 bg-muted/50 rounded w-32 animate-pulse"></div>
                <div className="h-3 bg-muted/50 rounded w-12 animate-pulse"></div>
              </div>
            ))
          ) : (
            <>
              <MachineCardMetric label={t("card.kpis.avgCycleTime")} value={formatKpiValue(truckLoading?.avgCycleTime, 'time')} />
              <MachineCardMetric label={t("card.kpis.targetCycleTime")} value={formatKpiValue(truckLoading?.targetCycleTime, 'time')} />
              <MachineCardMetric label={t("card.kpis.longCycles")} value={t("card.kpis.countWithPercent", { value: truckLoading?.longCyclesCnt ?? "-", percent: formatKpiValue(truckLoading?.longCyclesPerc, 'percentage') })} />
              <MachineCardMetric label={t("card.kpis.vsTarget")} value={formatKpiValue(truckLoading?.vsTarget, 'percentage')} />
              <MachineCardMetric label={t("card.kpis.bucketCount")} value={formatKpiValue(truckLoading?.bucketCount, 'number')} />
              <MachineCardMetric label={t("card.kpis.avgWaitTime")} value={formatKpiValue(waitingForTruck?.avgWaitTime, 'time')} />
              <MachineCardMetric label={t("card.kpis.longWaits")} value={t("card.kpis.countWithPercent", { value: waitingForTruck?.longWaitsCnt ?? "-", percent: formatKpiValue(waitingForTruck?.longWaitsPerc, 'percentage') })} />
              <MachineCardMetric label={t("card.kpis.boulderCount")} value={formatKpiValue(truckLoading?.boulderCount, 'number')} />
              <MachineCardMetric label={t("card.kpis.boulderImpact")} value={formatKpiValue(truckLoading?.boulderImpact, 'number')} />
            </>
          )}
        </div>


      </div>
    </div>
  );
};

interface MachineCardMetricProps {
  label: string;
  value: string | number;
}

const MachineCardMetric = ({ label, value }: MachineCardMetricProps) => {
  return (
    <div className="flex justify-between items-center text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export default MachineCard;